import React, { useRef, useState } from "react";
import { Button, ProgressBar } from "react-bootstrap";
import { toast } from "react-toastify";
import { iconsApi } from "../../helpers/tmClient";
import ImageWithFallback from "./ImageWithFallback";

/**
 * BulkIconImport — upload a ZIP of PNG icons and review the result.
 *
 * The archive is sent to the backend which extracts + hosts every valid
 * PNG and returns the imported icon objects WITHOUT creating an Icon Set.
 * The parent merges those icons into the editable Icons list so the admin
 * can tweak names / deep links / remove rows before saving.
 *
 * Provides the full modern flow: drag & drop / browse, upload progress,
 * server-side extraction progress, and a success / error summary.
 *
 * Props:
 *  - onImported(icons) : called with the imported icon array on success.
 *  - disabled          : disable interaction (e.g. while the set is saving).
 */
const PHASE = {
  IDLE: "idle",
  UPLOADING: "uploading",
  PROCESSING: "processing",
  DONE: "done",
  ERROR: "error",
};

const isZipFile = (file) =>
  !!file &&
  (/\.zip$/i.test(file.name) ||
    ["application/zip", "application/x-zip-compressed", "multipart/x-zip"].includes(
      file.type
    ));

const BulkIconImport = ({ onImported, disabled = false }) => {
  const inputRef = useRef(null);
  const [zipFile, setZipFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [phase, setPhase] = useState(PHASE.IDLE);
  const [uploadPct, setUploadPct] = useState(0);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const busy = phase === PHASE.UPLOADING || phase === PHASE.PROCESSING;
  const locked = disabled || busy;

  const pickFile = (file) => {
    if (!file) return;
    if (!isZipFile(file)) {
      setError("Please select a .zip archive.");
      setZipFile(null);
      return;
    }
    setError("");
    setResult(null);
    setPhase(PHASE.IDLE);
    setUploadPct(0);
    setZipFile(file);
  };

  const onInput = (e) => pickFile((e.target.files && e.target.files[0]) || null);

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (locked) return;
    pickFile(e.dataTransfer.files && e.dataTransfer.files[0]);
  };

  const reset = () => {
    setZipFile(null);
    setResult(null);
    setError("");
    setPhase(PHASE.IDLE);
    setUploadPct(0);
    if (inputRef.current) inputRef.current.value = "";
  };

  const startImport = async () => {
    if (!zipFile || busy) return;
    setError("");
    setResult(null);
    setPhase(PHASE.UPLOADING);
    setUploadPct(0);

    const fd = new FormData();
    fd.append("zip_file", zipFile);

    try {
      const res = await iconsApi.bulkImport(fd, (evt) => {
        if (evt && evt.total) {
          const pct = Math.round((evt.loaded / evt.total) * 100);
          setUploadPct(pct);
          // Once bytes are fully sent, the server is extracting + uploading.
          if (pct >= 100) setPhase(PHASE.PROCESSING);
        }
      });

      const data = res.data;
      if (data && data.status && data.data) {
        setResult(data.data);
        setPhase(PHASE.DONE);
        onImported && onImported(data.data.imported || []);
        toast.success(
          data.response_message ||
            `Imported ${data.data.summary.imported_count} icon(s)`,
          { position: "bottom-right", autoClose: 1200 }
        );
      } else {
        const msg = (data && data.response_message) || "Import failed";
        setError(msg);
        setPhase(PHASE.ERROR);
        toast.error(msg, { position: "bottom-right", autoClose: 1500 });
      }
    } catch (err) {
      const msg =
        (err && err.response && err.response.data &&
          err.response.data.response_message) ||
        "Import failed. Please try again.";
      setError(msg);
      setPhase(PHASE.ERROR);
      toast.error(msg, { position: "bottom-right", autoClose: 1500 });
    }
  };

  const imported = result?.imported || [];
  const skipped = result?.skipped || [];
  const previewIcons = imported.slice(0, 24);

  return (
    <div>
      {/* Dropzone */}
      <div
        onClick={() => !locked && inputRef.current && inputRef.current.click()}
        onDragOver={(e) => {
          e.preventDefault();
          if (!locked) setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={locked ? (e) => e.preventDefault() : onDrop}
        style={{
          border: `2px dashed ${error ? "#dc3545" : dragOver ? "#0093ed" : "#cfd4da"}`,
          borderRadius: 8,
          padding: zipFile ? 12 : 24,
          textAlign: "center",
          cursor: busy ? "default" : locked ? "not-allowed" : "pointer",
          background: busy ? "#f8f9fa" : locked ? "#f0f0f0" : dragOver ? "#f0f8ff" : "#fafbfc",
          opacity: disabled && !busy ? 0.65 : 1,
          transition: "all .15s ease",
        }}
      >
        {busy ? (
          /* ── uploading / processing state ── */
          <div style={{ cursor: "default" }} onClick={(e) => e.stopPropagation()}>
            <div className="d-flex align-items-center gap-2 mb-2">
              <i className="bx bxs-file-archive" style={{ fontSize: 22, color: "#0093ed", flexShrink: 0 }}></i>
              <div className="text-start text-truncate flex-grow-1" style={{ maxWidth: 280 }}>
                <div className="fw-medium text-truncate" style={{ fontSize: 13 }}>{zipFile.name}</div>
                <small className="text-muted">{(zipFile.size / (1024 * 1024)).toFixed(2)} MB</small>
              </div>
              <span className="spinner-border spinner-border-sm text-primary ms-auto" role="status" style={{ flexShrink: 0 }}></span>
            </div>
            {phase === PHASE.UPLOADING ? (
              <>
                <div className="d-flex justify-content-between small text-muted mb-1">
                  <span>Uploading…</span>
                  <span>{uploadPct}%</span>
                </div>
                <ProgressBar now={uploadPct} style={{ height: 6, borderRadius: 4 }} />
              </>
            ) : (
              <>
                <div className="small text-muted mb-1">Extracting &amp; importing icons…</div>
                <ProgressBar animated now={100} style={{ height: 6, borderRadius: 4 }} />
              </>
            )}
          </div>
        ) : zipFile ? (
          /* ── file selected, idle ── */
          <div className="d-flex align-items-center gap-2" onClick={(e) => e.stopPropagation()}>
            <i className="bx bxs-file-archive" style={{ fontSize: 26, color: "#0093ed", flexShrink: 0 }}></i>
            <div className="text-start text-truncate flex-grow-1">
              <div className="fw-medium text-truncate" style={{ maxWidth: 260 }}>{zipFile.name}</div>
              <small className="text-muted">{(zipFile.size / (1024 * 1024)).toFixed(2)} MB</small>
            </div>
            <button
              type="button"
              title="Remove file"
              className="btn btn-sm btn-icon btn-outline-danger"
              onClick={(e) => { e.stopPropagation(); reset(); }}
              style={{ flexShrink: 0 }}
            >
              <i className="bx bx-x"></i>
            </button>
          </div>
        ) : (
          /* ── empty / drag-drop ── */
          <div className="text-muted">
            <i className="bx bxs-cloud-upload" style={{ fontSize: 28 }}></i>
            <div>Drag &amp; drop a .zip file or click to browse</div>
            <small>ZIP containing PNG icons only — up to 100MB</small>
          </div>
        )}
        <input
          ref={inputRef}
          type="file"
          accept=".zip,application/zip,application/x-zip-compressed"
          onChange={onInput}
          disabled={locked}
          hidden
        />
      </div>

      {error && <div className="text-danger small mt-2">{error}</div>}

      {/* Actions */}
      <div className="d-flex align-items-center gap-2 mt-3">
        <Button
          variant="primary"
          size="sm"
          onClick={startImport}
          disabled={!zipFile || locked}
          className="d-inline-flex align-items-center gap-2"
        >
          {busy && (
            <span className="spinner-border spinner-border-sm" role="status"></span>
          )}
          {phase === PHASE.UPLOADING
            ? "Uploading..."
            : phase === PHASE.PROCESSING
            ? "Importing..."
            : "Import Icons"}
        </Button>
        {(result || error) && !busy && (
          <Button variant="outline-secondary" size="sm" onClick={reset}>
            Import another ZIP
          </Button>
        )}
      </div>

      {/* Success summary */}
      {phase === PHASE.DONE && result && (
        <div className="mt-3">
          <div className="alert alert-success py-2 mb-2">
            <i className="bx bx-check-circle me-1"></i>
            <strong>Successfully Imported: {result.summary.imported_count} Icon(s)</strong>
            {result.summary.imported_count > 0 && (
              <span className="ms-1 text-muted">
                — review them in the Icons list below before saving.
              </span>
            )}
          </div>

          {previewIcons.length > 0 && (
            <div className="d-flex flex-wrap gap-2 mb-2">
              {previewIcons.map((ic, i) => (
                <div
                  key={i}
                  className="border rounded p-1 text-center"
                  style={{ width: 64 }}
                  title={ic.icon_name}
                >
                  <ImageWithFallback
                    src={ic.icon_image}
                    alt={ic.icon_name}
                    className="rounded-2"
                    style={{ width: 40, height: 40, objectFit: "contain" }}
                  />
                  <div
                    className="small text-truncate mt-1"
                    style={{ fontSize: 10 }}
                  >
                    {ic.icon_name}
                  </div>
                </div>
              ))}
              {imported.length > previewIcons.length && (
                <div
                  className="border rounded d-flex align-items-center justify-content-center text-muted"
                  style={{ width: 64, height: 64 }}
                >
                  +{imported.length - previewIcons.length}
                </div>
              )}
            </div>
          )}

          {skipped.length > 0 && (
            <div className="alert alert-warning py-2 mb-0">
              <strong>Skipped: {skipped.length}</strong>
              <ul className="mb-0 mt-1 ps-3 small">
                {skipped.slice(0, 30).map((s, i) => (
                  <li key={i}>
                    {s.name} <span className="text-muted">— {s.reason}</span>
                  </li>
                ))}
                {skipped.length > 30 && (
                  <li className="text-muted">…and {skipped.length - 30} more</li>
                )}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Error summary */}
      {phase === PHASE.ERROR && error && (
        <div className="alert alert-danger py-2 mt-3 mb-0">
          <i className="bx bx-error-circle me-1"></i>
          {error}
        </div>
      )}
    </div>
  );
};

export default BulkIconImport;
